import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import api from '../api/axios';
import { Plus, Search, ChevronDown, ChevronRight } from 'lucide-react';

const statusBadge = s => {
  const map = { IN_PROGRESS:'badge-green', COMPLETED:'badge-muted', PAUSED:'badge-amber' };
  return <span className={`badge ${map[s]||'badge-muted'}`}>{s?.replace('_',' ')}</span>;
};

const SessionRow = ({ session, onResume, onComplete, onEdit, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const [entries, setEntries]   = useState([]);
  const [loading, setLoading]   = useState(false);

  const toggle = async () => {
    if (!expanded) {
      setLoading(true);
      try {
        const res = await api.get(`/operations/sessions/${session.id}/`);
        setEntries(res.data?.entries || res.data?.data?.entries || []);
      } catch { setEntries([]); }
      finally { setLoading(false); }
    }
    setExpanded(!expanded);
  };

  // Parse work entries from notes
  let workEntries = [];
  try {
    const parsed = JSON.parse(session.notes || '{}');
    workEntries = parsed.day_entries || [];
  } catch {}

  return (
    <div style={{ borderBottom: '1px solid var(--border-light)' }}>
      {/* Main Row */}
      <div className="session-row-grid" style={{ padding:'12px 16px', background: expanded?'var(--bg-hover)':'var(--white)', cursor:'pointer' }} onClick={toggle}>
        <div style={{ color:'var(--text-muted)', fontWeight:700, fontSize:12 }}>
          {expanded ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
        </div>
        <div>
          <div style={{ color:'var(--brand)', fontWeight:700, fontSize:13 }} className="session-cell-truncate">{session.session_number}</div>
          <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:2 }}>
            {session.direction&&`${session.direction} · `}{session.production_stage||'—'}
          </div>
        </div>
        <div style={{ fontSize:12, color:'var(--text-secondary)' }} className="session-cell-truncate">{session.block_reference||'—'}</div>
        <div style={{ fontSize:12, color:'var(--text-muted)' }} className="session-cell-truncate">{session.location||'—'}</div>
        <div style={{ fontSize:12, color:'var(--text-muted)' }}>{session.started_date}</div>
        <div>{statusBadge(session.status)}</div>
        <div className="session-row-actions" style={{ display:'flex', gap:6 }} onClick={e=>e.stopPropagation()}>
          {session.status==='IN_PROGRESS'&&<>
            <button className="btn btn-sm btn-secondary" onClick={onComplete}>Complete</button>
          </>}
          <button className="btn btn-sm btn-secondary" onClick={onEdit}>Edit</button>
          <button className="btn btn-sm btn-danger" onClick={onDelete}>Delete</button>
        </div>
      </div>

      {/* Expanded — Day entries grouped by date */}
      {expanded && (
        <div style={{ background:'var(--bg-primary)', borderTop:'1px solid var(--border-light)', paddingLeft:28 }}>
          {loading && <div style={{ padding:'12px 16px', fontSize:13, color:'var(--text-muted)' }}>Loading...</div>}

          {/* Work entries from notes — grouped by date */}
          {!loading && workEntries.length > 0 && (() => {
            // Group entries by date
            const grouped = workEntries.filter(e=>e.work_type).reduce((acc, e) => {
              const date = e.entry_date || 'Unknown Date';
              if(!acc[date]) acc[date] = [];
              acc[date].push(e);
              return acc;
            }, {});
            return (
              <div>
                {Object.entries(grouped).map(([date, dayEntries]) => (
                  <div key={date}>
                    {/* Date header */}
                    <div style={{ padding:'6px 16px', background:'var(--brand-dim)', fontSize:11, fontWeight:700, color:'var(--brand)', borderBottom:'1px solid var(--border-light)' }}>
                      📅 {date}
                    </div>
                    {dayEntries.map((entry, i) => (
                      <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8, padding:'10px 16px', borderBottom:'1px solid var(--border-light)', alignItems:'center' }}>
                        <div style={{ fontSize:13, fontWeight:600, color:'var(--text-primary)' }}>
                          {entry.work_type.replace('_',' ')}
                        </div>
                        <div style={{ fontSize:12, color:'var(--text-muted)' }}>
                          {entry.data?.category || entry.data?.cutting_type || entry.data?.blasting_type || '—'}
                        </div>
                        <div>
                          <span className={`badge ${entry.status==='COMPLETED'?'badge-green':entry.status==='ON_HOLD'?'badge-amber':'badge-blue'}`}>
                            {entry.status==='COMPLETED'?'✓ Completed':entry.status==='ON_HOLD'?'On Hold':'In Progress'}
                          </span>
                          {entry.hold_reason && <span style={{ fontSize:11, color:'var(--text-muted)', marginLeft:6 }}>{entry.hold_reason}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            );
          })()}

          {/* DB entries */}
          {!loading && entries.length > 0 && (
            <div>
              {Object.entries(entries.reduce((acc,e)=>{
                const d = e.entry_date||e.created_at?.split('T')[0]||'Unknown';
                if(!acc[d])acc[d]=[];
                acc[d].push(e); return acc;
              },{})).map(([date, dayEntries]) => (
                <div key={date}>
                  <div style={{ padding:'6px 16px', background:'var(--brand-dim)', fontSize:11, fontWeight:700, color:'var(--brand)' }}>{date}</div>
                  {dayEntries.map((e,i) => (
                    <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:8, padding:'8px 16px', borderBottom:'1px solid var(--border-light)', alignItems:'center' }}>
                      <div style={{ fontSize:12 }}>{e.shift||'—'}</div>
                      <div style={{ fontSize:12, color:'var(--text-muted)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{e.progress_notes||e.notes||'—'}</div>
                      <div style={{ fontSize:12, color:'var(--text-muted)' }}>{e.worker_count?`${e.worker_count} workers`:'—'}</div>
                      <div><span className={`badge ${e.is_completed?'badge-green':'badge-amber'}`}>{e.is_completed?'Completed':'In Progress'}</span></div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}

          {!loading && workEntries.length===0 && entries.length===0 && (
            <div style={{ padding:'12px 16px', fontSize:13, color:'var(--text-muted)' }}>No entries yet. Click Resume to add today's work.</div>
          )}
        </div>
      )}
    </div>
  );
};

export default function OperationsPage() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [filter, setFilter]     = useState('');
  const [msg, setMsg]           = useState('');
  const [error, setError]       = useState('');

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/operations/sessions/?search=${search}${filter?`&status=${filter}`:''}&ordering=-started_date`)
      .then(r => setSessions(r.data.results||[]))
      .finally(() => setLoading(false));
  }, [search, filter]);

  useEffect(() => { load(); }, [load]);

  const activeSessions = sessions.filter(s => s.status==='IN_PROGRESS');

  const handleResume = (session) => navigate(`/operations/resume/${session.id}`);
  const handleEdit   = (session) => navigate(`/operations/edit/${session.id}`);

  const handleComplete = async (id, number) => {
    if (!window.confirm(`Mark ${number} as COMPLETED?`)) return;
    try {
      await api.post(`/operations/sessions/${id}/complete/`);
      setMsg(`${number} completed!`); setTimeout(()=>setMsg(''),3000); load();
    } catch { setError('Failed.'); }
  };

  const handleDelete = async (id, number) => {
    if (!window.confirm(`Permanently delete ${number}? This cannot be undone.`)) return;
    try {
      await api.delete(`/operations/sessions/${id}/delete/`);
      setMsg(`${number} deleted.`); setTimeout(()=>setMsg(''),3000); load();
    } catch { setError('Delete failed. You may not have permission.'); setTimeout(()=>setError(''),4000); }
  };

  return (
    <Layout title="Work Sessions" subtitle="All operations — day wise entries"
      actions={<button className="btn btn-primary" onClick={()=>navigate('/operations/new')}><Plus size={14}/> New Work Session</button>}
    >
      {msg   && <div className="success-box mb-16">{msg}</div>}
      {error && <div className="error-box mb-16">{error}</div>}

      {/* KPIs */}
      <div className="kpi-grid" style={{ marginBottom:16 }}>
        <div className="kpi-card green"><div className="kpi-value">{sessions.filter(s=>s.status==='IN_PROGRESS').length}</div><div className="kpi-label">Active</div></div>
        <div className="kpi-card blue"><div className="kpi-value">{sessions.filter(s=>s.status==='COMPLETED').length}</div><div className="kpi-label">Completed</div></div>
        <div className="kpi-card amber"><div className="kpi-value">{activeSessions.length}</div><div className="kpi-label">Can Resume</div></div>
        <div className="kpi-card brand"><div className="kpi-value">{sessions.length}</div><div className="kpi-label">Total</div></div>
      </div>

      {/* Sessions Table */}
      <div className="card">
        <div className="filter-bar">
          <div className="search-wrap">
            <Search size={14}/>
            <input className="search-input" placeholder="Search session, location, block..." value={search} onChange={e=>setSearch(e.target.value)}/>
          </div>
          <select className="form-input" style={{ width:'auto', minWidth:160 }} value={filter} onChange={e=>setFilter(e.target.value)}>
            <option value="">All Status</option>
            <option value="IN_PROGRESS">In Progress</option>
            <option value="COMPLETED">Completed</option>
            <option value="PAUSED">Paused</option>
          </select>
        </div>

        {loading ? <div className="loading"><div className="spinner"/>Loading...</div> :
          sessions.length === 0 ? (
            <div className="empty-state">
              <p>No sessions found.</p>
              <button className="btn btn-primary" style={{ marginTop:16 }} onClick={()=>navigate('/operations/new')}>
                <Plus size={14}/> Start First Session
              </button>
            </div>
          ) : (
            <div>
              {/* Column Headers */}
              <div className="session-header-row" style={{ padding:'8px 16px', borderBottom:'2px solid var(--border)', background:'var(--bg-primary)' }}> <div/>
                {['Session #','Block Ref','Location','Date','Status'].map(h=>(
                  <div key={h} style={{ fontSize:10, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.5px' }}>{h}</div>
                ))}
                <div style={{ fontSize:10, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.5px' }}>Actions</div>
              </div>
              {sessions.map(s => (
                <SessionRow key={s.id} session={s}
                  onResume={()=>handleResume(s)}
                  onComplete={()=>handleComplete(s.id, s.session_number)}
                  onEdit={()=>handleEdit(s)}
                  onDelete={()=>handleDelete(s.id, s.session_number)}
                />
              ))}
            </div>
          )
        }
      </div>
    </Layout>
  );
}
